import { useEffect, useState } from 'react';
import {
  formatSimulationClock,
  SIMULATION_TIME_SCALE_PRESETS,
  type SimulationTimeScale,
} from '../../lib/simulationDuration';
import {
  getTargetLiveFrame,
  subscribeLiveFrames,
} from '../../lib/liveFrameAnimation';
import { useSimulationStore } from '../../store/simulationStore';

interface Props {
  compact?: boolean;
}

export default function SimulationPlaybackControls({ compact = false }: Props) {
  const runStatus = useSimulationStore((s) => s.run.status);
  const runMode = useSimulationStore((s) => s.run.runMode);
  const liveSimTime = useSimulationStore((s) => s.run.liveSimTime);
  const timeScale = useSimulationStore((s) => s.simulationTimeScale);
  const setSimulationTimeScale = useSimulationStore(
    (s) => s.setSimulationTimeScale,
  );
  const [, setTick] = useState(0);

  useEffect(() => subscribeLiveFrames(() => setTick((n) => n + 1)), []);

  if (runStatus !== 'running' || runMode !== 'realtime') {
    return null;
  }

  const frame = getTargetLiveFrame();
  const simTime = frame?.sim_time ?? liveSimTime;

  return (
    <div
      className={`flex items-center gap-2 ${compact ? '' : 'rounded-lg border border-border/60 bg-surface px-2 py-1'}`}
      role="group"
      aria-label="Simulation playback"
    >
      <div className="flex min-w-[3.5rem] flex-col leading-none">
        <span className="text-[9px] uppercase tracking-wide text-text-muted">
          Sim time
        </span>
        <span
          className="font-mono text-sm font-semibold tabular-nums text-text"
          title={`${Math.round(simTime * 10) / 10} simulation seconds`}
        >
          {formatSimulationClock(simTime)}
        </span>
      </div>
      <span className="h-6 w-px bg-border/70" aria-hidden />
      <div className="flex items-center gap-0.5 rounded-md border border-border/60 bg-surface-raised p-0.5">
        {SIMULATION_TIME_SCALE_PRESETS.map((speed) => {
          const active = timeScale === speed;
          return (
            <button
              key={speed}
              type="button"
              className={`rounded px-2 py-0.5 text-[10px] font-semibold tabular-nums transition ${
                active
                  ? 'bg-accent text-white shadow-sm'
                  : 'text-text-muted hover:bg-surface-overlay hover:text-text'
              }`}
              aria-pressed={active}
              title={`Run simulation at ${speed}× speed`}
              onClick={() => setSimulationTimeScale(speed as SimulationTimeScale)}
            >
              ×{speed}
            </button>
          );
        })}
      </div>
    </div>
  );
}
